import React, { createContext, useContext, useReducer } from 'react';

// Define application states
export const APP_STATES = {
  NOTHING_SELECTED: 'nothing_selected',
  HEATMAP_LOCATION_SELECTED: 'heatmap_location_selected',
  HEX_SELECTED: 'hex_selected',
  LOADING_ORIGINS: 'loading_origins',
  ORIGINS_DISPLAYED: 'origins_displayed',
  POSTCODE_SELECTED: 'postcode_selected',
  LOADING_DESTINATIONS: 'loading_destinations',
  DESTINATIONS_LOADED: 'destinations_loaded',
  ERROR: 'error'
};

// Define UI configuration for each state
export const UI_CONFIG = {
  [APP_STATES.NOTHING_SELECTED]: {
    exploreOriginsEnabled: false,
    clearMapEnabled: false,
    filtersEnabled: false,
    postcodeActionsEnabled: false,
    helpMessage: "Click on the map to load the heatmap"
  },
  [APP_STATES.HEATMAP_LOCATION_SELECTED]: {
    exploreOriginsEnabled: false,
    clearMapEnabled: true,
    filtersEnabled: false,
    postcodeActionsEnabled: false,
    helpMessage: "Select a hex to explore where visitors come from"
  },
  [APP_STATES.HEX_SELECTED]: {
    exploreOriginsEnabled: true,
    clearMapEnabled: true,
    filtersEnabled: false,
    postcodeActionsEnabled: false,
    helpMessage: "Click 'Explore Origins' to see visitor postcodes"
  },
  [APP_STATES.LOADING_ORIGINS]: {
    exploreOriginsEnabled: false,
    clearMapEnabled: false,
    filtersEnabled: false,
    postcodeActionsEnabled: false,
    helpMessage: "Loading origin data..."
  },
  [APP_STATES.ORIGINS_DISPLAYED]: {
    exploreOriginsEnabled: true,
    clearMapEnabled: true,
    filtersEnabled: true,
    postcodeActionsEnabled: true,
    helpMessage: "Click a postcode sector to see where visitors travel to"
  },
  [APP_STATES.POSTCODE_SELECTED]: {
    exploreOriginsEnabled: true,
    clearMapEnabled: true,
    filtersEnabled: true,
    postcodeActionsEnabled: true,
    helpMessage: "View postcode demographics and destinations"
  },
  [APP_STATES.LOADING_DESTINATIONS]: {
    exploreOriginsEnabled: false,
    clearMapEnabled: false,
    filtersEnabled: false,
    postcodeActionsEnabled: false,
    helpMessage: "Loading destination data..."
  },
  [APP_STATES.DESTINATIONS_LOADED]: {
    exploreOriginsEnabled: false,
    clearMapEnabled: true,
    filtersEnabled: true,
    postcodeActionsEnabled: true,
    helpMessage: "View postcode destinations on the map"
  },
  [APP_STATES.ERROR]: {
    exploreOriginsEnabled: false,
    clearMapEnabled: true,
    filtersEnabled: false,
    postcodeActionsEnabled: false,
    helpMessage: "An error occurred. Clear the map to start over."
  }
};

// Initial state
const initialState = {
  currentState: APP_STATES.NOTHING_SELECTED,
  selectedHex: null,
  selectedPostcode: null,
  error: null,
  // Additional context data
  heatmapLocation: null
};

// Reducer to handle state transitions
const appStateReducer = (state, action) => {
  switch (action.type) {
    case 'HEATMAP_CLICKED':
      return {
        ...state,
        currentState: APP_STATES.HEATMAP_LOCATION_SELECTED,
        heatmapLocation: action.location,
        selectedHex: null,
        selectedPostcode: null,
        error: null
      };

    case 'HEX_CLICKED':
      return {
        ...state,
        currentState: APP_STATES.HEX_SELECTED,
        selectedHex: action.hex,
        selectedPostcode: null,
        error: null
      };

    case 'START_LOADING_ORIGINS':
      return {
        ...state,
        currentState: APP_STATES.LOADING_ORIGINS,
        selectedPostcode: null,
        error: null
      };

    case 'ORIGINS_LOADED':
      return {
        ...state,
        currentState: APP_STATES.ORIGINS_DISPLAYED,
        error: null
      };

    case 'POSTCODE_CLICKED':
      return {
        ...state,
        currentState: APP_STATES.POSTCODE_SELECTED,
        selectedPostcode: action.postcode,
        error: null
      };

    case 'START_LOADING_DESTINATIONS':
      return {
        ...state,
        currentState: APP_STATES.LOADING_DESTINATIONS,
        error: null
      };

    case 'DESTINATIONS_LOADED':
      return {
        ...state,
        currentState: APP_STATES.DESTINATIONS_LOADED,
        error: null
      };

    case 'CLEAR_MAP':
      return {
        ...initialState
      };

    case 'ERROR':
      return {
        ...state,
        currentState: APP_STATES.ERROR,
        error: action.error
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        currentState: APP_STATES.HEATMAP_LOCATION_SELECTED,
        error: null
      };

    default:
      return state;
  }
};

// Create context
const AppStateContext = createContext();

// Provider component
export const AppStateProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appStateReducer, initialState);

  // Get UI configuration for current state
  const uiConfig = UI_CONFIG[state.currentState];

  const value = {
    // Current state
    currentState: state.currentState,
    selectedHex: state.selectedHex,
    selectedPostcode: state.selectedPostcode,
    heatmapLocation: state.heatmapLocation,
    error: state.error,

    // UI configuration
    ui: uiConfig,

    // Dispatch function
    dispatch
  };

  return (
    <AppStateContext.Provider value={value}>
      {children}
    </AppStateContext.Provider>
  );
};

// Custom hook to use app state
export const useAppState = () => {
  const context = useContext(AppStateContext);
  if (!context) {
    throw new Error('useAppState must be used within AppStateProvider');
  }
  return context;
};

// Helper functions for common state transitions
export const appStateActions = {
  heatmapClicked: (location) => ({ type: 'HEATMAP_CLICKED', location }),
  hexClicked: (hex) => ({ type: 'HEX_CLICKED', hex }),
  startLoadingOrigins: () => ({ type: 'START_LOADING_ORIGINS' }),
  originsLoaded: () => ({ type: 'ORIGINS_LOADED' }),
  postcodeClicked: (postcode) => ({ type: 'POSTCODE_CLICKED', postcode }),
  startLoadingDestinations: () => ({ type: 'START_LOADING_DESTINATIONS' }),
  destinationsLoaded: () => ({ type: 'DESTINATIONS_LOADED' }),
  clearMap: () => ({ type: 'CLEAR_MAP' }),
  error: (error) => ({ type: 'ERROR', error }),
  clearError: () => ({ type: 'CLEAR_ERROR' })
};
